import { ExerciseGroupType } from '../../types/workout/group-type';

/**
 * Forma mínima que necesita el listado. Se tipa laxo a propósito porque las
 * cards reciben tanto grupos de instancia como de plantilla, que comparten esta
 * estructura pero son interfaces distintas.
 */
interface ListableExercise {
  id?: number;
  rowIndex?: number;
  exercise?: { name?: string } | null;
}

interface ListableGroup {
  groupNumber?: number;
  groupType?: ExerciseGroupType | string;
  isRestPeriod?: boolean;
  exercises?: ListableExercise[] | null;
}

export interface PlanExerciseListItem {
  key: string;
  /** Posición 1..N dentro del entrenamiento completo. */
  position: number;
  /**
   * Etiqueta de planilla: letra del grupo + orden dentro del grupo (A1, A2, B1).
   *
   * #10: el correlativo plano 1..N aplanaba los grupos, que es justo lo que el
   * coach necesita distinguir para leer una superserie.
   */
  label: string;
  name: string;
}

/** 0 -> A, 25 -> Z, 26 -> AA, 27 -> AB. Los planes largos pasan de 26 grupos. */
function groupLetter(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function isRestGroup(group: ListableGroup): boolean {
  return group.groupType === ExerciseGroupType.REST || group.isRestPeriod === true;
}

/**
 * Aplana los grupos de un entrenamiento en la lista ordenada de ejercicios
 * que se hacen, para el toggle de la card de planes (#10).
 *
 * Orden: primero por `groupNumber` del grupo y después por `rowIndex` del
 * ejercicio dentro del grupo, que es el orden real en que se ejecutan. Los
 * grupos de descanso se omiten: no son ejercicios. Cuando falta `groupNumber`
 * o `rowIndex` se cae al orden en que vinieron del backend, sin reordenar.
 */
export function flattenPlanExercises(groups: ListableGroup[] | null | undefined): PlanExerciseListItem[] {
  if (!groups || groups.length === 0) return [];

  const ordered = groups
    .map((group, groupIdx) => ({ group, groupIdx }))
    .filter(({ group }) => !isRestGroup(group))
    .sort((a, b) => (a.group.groupNumber ?? a.groupIdx) - (b.group.groupNumber ?? b.groupIdx));

  const items: PlanExerciseListItem[] = [];

  // La letra sigue el orden de EJECUCIÓN (posición en `ordered`), no el orden en
  // que los grupos vinieron del backend ni su groupNumber, que puede tener huecos.
  ordered.forEach(({ group, groupIdx }, orderedIdx) => {
    const exercises = [...(group.exercises ?? [])]
      .map((exercise, exerciseIdx) => ({ exercise, exerciseIdx }))
      .sort((a, b) => (a.exercise.rowIndex ?? a.exerciseIdx) - (b.exercise.rowIndex ?? b.exerciseIdx));

    const letter = groupLetter(orderedIdx);
    let positionInGroup = 0;

    exercises.forEach(({ exercise, exerciseIdx }) => {
      const name = exercise.exercise?.name?.trim();
      if (!name) return;
      positionInGroup += 1;
      items.push({
        key: `${exercise.id ?? `${groupIdx}-${exerciseIdx}`}`,
        position: items.length + 1,
        label: `${letter}${positionInGroup}`,
        name
      });
    });
  });

  return items;
}
